/**
 * Transition Rules: EnergyPlus 22.1 to 22.2
 */

import type { VersionTransition, ObjectTransitionRule, MultiObjectResult, TransitionChange } from '../types';
import type { IDFObject, IDFDocument, IDFField } from '../../parser/types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';
import { copyField, createObject } from '../engine';

/**
 * Transform Coil:Cooling:DX:MultiSpeed - Insert blank field within each speed data block
 *
 * For each speed (up to 4 speeds), there are per-speed fields. Need to insert
 * a new blank field (crankcase heater capacity curve name) at specific position within each speed.
 *
 * Old format per speed (starting at field 14 for speed 1):
 * - Gross Rated Total Cooling Capacity
 * - Gross Rated SHR
 * - Gross Rated COP
 * - Rated Air Flow Rate
 * - Rated Evaporator Fan Power Per Volume Flow Rate 2017
 * - Rated Evaporator Fan Power Per Volume Flow Rate 2023
 * - Total Cooling Capacity Function of Temperature Curve
 * - Total Cooling Capacity Function of Flow Fraction Curve
 * - Energy Input Ratio Function of Temperature Curve
 * - Energy Input Ratio Function of Flow Fraction Curve
 * - Part Load Fraction Correlation Curve
 * - Nominal Time for Condensate Removal to Begin
 * - Ratio of Initial Moisture Evaporation Rate and Steady State Latent Capacity
 * - Maximum Cycling Rate
 * - Latent Capacity Time Constant
 * - Rated Waste Heat Fraction of Power Input
 * - Waste Heat Function of Temperature Curve
 * - Evaporative Condenser Effectiveness
 * - Evaporative Condenser Air Flow Rate
 * - Rated Evaporative Condenser Pump Power Consumption
 *
 * New format adds a blank field after field 5 (Rated Evaporator Fan Power Per Volume Flow Rate 2017)
 */
function transformCoolingMultiSpeed(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const newFields: IDFField[] = [];
  const objectName = object.fields[0]?.value;

  // Copy header fields (0-13) - fields before speed data
  for (let i = 0; i <= 13; i++) {
    if (object.fields[i]) {
      newFields.push(copyField(object.fields[i]));
    }
  }

  // Process per-speed data
  const oldFieldsPerSpeed = 20;
  const speedStartIndex = 14;
  const maxSpeeds = 4;

  for (let speed = 0; speed < maxSpeeds; speed++) {
    const oldStart = speedStartIndex + speed * oldFieldsPerSpeed;
    const hasSpeedData = object.fields[oldStart]?.value.trim();

    if (!hasSpeedData && speed > 0) {
      break; // No more speeds defined
    }

    // Copy first 6 fields of speed data (indices 0-5)
    for (let i = 0; i < 6; i++) {
      if (object.fields[oldStart + i]) {
        newFields.push(copyField(object.fields[oldStart + i]));
      }
    }

    // Insert new blank field for crankcase heater capacity curve name
    newFields.push({ value: '' });

    // Copy remaining fields of speed data (indices 6-19)
    for (let i = 6; i < oldFieldsPerSpeed; i++) {
      if (object.fields[oldStart + i]) {
        newFields.push(copyField(object.fields[oldStart + i]));
      }
    }
  }

  result.primary = createObject(
    object.id,
    object.className,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  const change: TransitionChange = {
    type: 'field_added',
    objectClass: object.className,
    description: `Added crankcase heater capacity curve name field to each speed data block`,
  };
  if (objectName !== undefined) {
    change.objectName = objectName;
  }
  result.changes.push(change);

  return result;
}

/**
 * Transform Coil:Heating:DX:MultiSpeed - Insert blank field within each speed data block
 *
 * Similar to cooling multi-speed, but for heating coils
 */
function transformHeatingMultiSpeed(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const newFields: IDFField[] = [];
  const objectName = object.fields[0]?.value;

  // Copy header fields (0-12) - fields before speed data
  for (let i = 0; i <= 12; i++) {
    if (object.fields[i]) {
      newFields.push(copyField(object.fields[i]));
    }
  }

  // Process per-speed data
  const oldFieldsPerSpeed = 12;
  const speedStartIndex = 13;
  const maxSpeeds = 4;

  for (let speed = 0; speed < maxSpeeds; speed++) {
    const oldStart = speedStartIndex + speed * oldFieldsPerSpeed;
    const hasSpeedData = object.fields[oldStart]?.value.trim();

    if (!hasSpeedData && speed > 0) {
      break; // No more speeds defined
    }

    // Copy first 5 fields of speed data (indices 0-4)
    for (let i = 0; i < 5; i++) {
      if (object.fields[oldStart + i]) {
        newFields.push(copyField(object.fields[oldStart + i]));
      }
    }

    // Insert new blank field
    newFields.push({ value: '' });

    // Copy remaining fields of speed data (indices 5-11)
    for (let i = 5; i < oldFieldsPerSpeed; i++) {
      if (object.fields[oldStart + i]) {
        newFields.push(copyField(object.fields[oldStart + i]));
      }
    }
  }

  result.primary = createObject(
    object.id,
    object.className,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  const change: TransitionChange = {
    type: 'field_added',
    objectClass: object.className,
    description: `Added crankcase heater capacity curve name field to each speed data block`,
  };
  if (objectName !== undefined) {
    change.objectName = objectName;
  }
  result.changes.push(change);

  return result;
}

export function createTransition_22_1_to_22_2(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // Coil:Cooling:DX:CurveFit:Speed - Insert blank field at position 9
  objectRules.set('COIL:COOLING:DX:CURVEFIT:SPEED', {
    sourceClass: 'Coil:Cooling:DX:CurveFit:Speed',
    targetClass: 'Coil:Cooling:DX:CurveFit:Speed',
    fieldMappings: [
      ...createCopyMappings(0, 8), // Fields 0-7
      { sourceIndex: -1, targetIndex: 8, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(8, 9, 30),
    ],
  });

  // Coil:Cooling:DX:SingleSpeed - Insert blank field at position 8
  objectRules.set('COIL:COOLING:DX:SINGLESPEED', {
    sourceClass: 'Coil:Cooling:DX:SingleSpeed',
    targetClass: 'Coil:Cooling:DX:SingleSpeed',
    fieldMappings: [
      ...createCopyMappings(0, 7), // Fields 0-6
      { sourceIndex: -1, targetIndex: 7, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(7, 8, 40),
    ],
  });

  // Coil:Heating:DX:SingleSpeed - Insert blank field at position 6
  objectRules.set('COIL:HEATING:DX:SINGLESPEED', {
    sourceClass: 'Coil:Heating:DX:SingleSpeed',
    targetClass: 'Coil:Heating:DX:SingleSpeed',
    fieldMappings: [
      ...createCopyMappings(0, 5), // Fields 0-4
      { sourceIndex: -1, targetIndex: 5, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(5, 6, 40),
    ],
  });

  // FuelFactors - Remove fields 2-3
  objectRules.set('FUELFACTORS', {
    sourceClass: 'FuelFactors',
    targetClass: 'FuelFactors',
    fieldMappings: [
      { sourceIndex: 0, targetIndex: 0, type: 'copy' },
      { sourceIndex: 1, targetIndex: -1, type: 'delete' },
      { sourceIndex: 2, targetIndex: -1, type: 'delete' },
      ...createShiftedCopyMappings(3, 1, 30),
    ],
  });

  // Space - Insert two "autocalculate" fields at positions 3-4
  objectRules.set('SPACE', {
    sourceClass: 'Space',
    targetClass: 'Space',
    fieldMappings: [
      ...createCopyMappings(0, 2), // Fields 0-1
      { sourceIndex: -1, targetIndex: 2, type: 'add_default', defaultValue: 'autocalculate' },
      { sourceIndex: -1, targetIndex: 3, type: 'add_default', defaultValue: 'autocalculate' },
      ...createShiftedCopyMappings(2, 4, 20),
    ],
  });

  // Coil:Cooling:WaterToAirHeatPump:EquationFit - Insert 3 numeric fields at positions 11-13
  objectRules.set('COIL:COOLING:WATERTOAIRHEATPUMP:EQUATIONFIT', {
    sourceClass: 'Coil:Cooling:WaterToAirHeatPump:EquationFit',
    targetClass: 'Coil:Cooling:WaterToAirHeatPump:EquationFit',
    fieldMappings: [
      ...createCopyMappings(0, 10), // Fields 0-9
      { sourceIndex: -1, targetIndex: 10, type: 'add_default', defaultValue: '30.0' },
      { sourceIndex: -1, targetIndex: 11, type: 'add_default', defaultValue: '27.0' },
      { sourceIndex: -1, targetIndex: 12, type: 'add_default', defaultValue: '19.0' },
      ...createShiftedCopyMappings(10, 13, 30),
    ],
  });

  // Coil:Heating:WaterToAirHeatPump:EquationFit - Insert 3 numeric fields at positions 10-12
  objectRules.set('COIL:HEATING:WATERTOAIRHEATPUMP:EQUATIONFIT', {
    sourceClass: 'Coil:Heating:WaterToAirHeatPump:EquationFit',
    targetClass: 'Coil:Heating:WaterToAirHeatPump:EquationFit',
    fieldMappings: [
      ...createCopyMappings(0, 9), // Fields 0-8
      { sourceIndex: -1, targetIndex: 9, type: 'add_default', defaultValue: '20.0' },
      { sourceIndex: -1, targetIndex: 10, type: 'add_default', defaultValue: '20.0' },
      { sourceIndex: -1, targetIndex: 11, type: 'add_default', defaultValue: '1.0' },
      ...createShiftedCopyMappings(9, 12, 30),
    ],
  });

  // Coil:Cooling:DX:MultiSpeed - Per-speed field insertion
  objectRules.set('COIL:COOLING:DX:MULTISPEED', {
    sourceClass: 'Coil:Cooling:DX:MultiSpeed',
    targetClass: 'Coil:Cooling:DX:MultiSpeed',
    fieldMappings: [],
    multiObjectTransform: transformCoolingMultiSpeed,
  });

  // Coil:Heating:DX:MultiSpeed - Per-speed field insertion
  objectRules.set('COIL:HEATING:DX:MULTISPEED', {
    sourceClass: 'Coil:Heating:DX:MultiSpeed',
    targetClass: 'Coil:Heating:DX:MultiSpeed',
    fieldMappings: [],
    multiObjectTransform: transformHeatingMultiSpeed,
  });

  return {
    fromVersion: { major: 22, minor: 1 },
    toVersion: { major: 22, minor: 2 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
